import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import '../CSS/LeaveStatus.css';
import { fetchEmployeeLeaves, fetchLeaveSummary } from '../features/leave/leaveThunks';
import Sidebar from './Sidebar';

// Label maps
const LEAVE_TYPE_LABELS = {
  CASUAL:  'Casual Leave',
  MEDICAL: 'Medical Leave',
  EARNED:  'Earned Leave',
};

const PLANNING_LABELS = {
  PLANNED:     'Planned',
  UNPLANNED:   'Unplanned',
  LAST_MINUTE: 'Last Minute',
};

const DURATION_LABELS = {
  FULL_DAY: 'Full Day',
  HALF_DAY: 'Half Day',
};

// Status derivation
const getLeaveStatus = (leave) => {
  switch (leave.status) {
    case 'APPROVED':
      return { label: 'Approved', className: 'approved' };
    case 'REJECTED_BY_DEPT_HEAD':
      return { label: 'Rejected by Dept Head', className: 'rejected' };
    case 'REJECTED_BY_HR':
      return { label: 'Rejected by HR', className: 'rejected' };
    case 'PENDING_HR':
      return { label: 'Pending HR', className: 'pending' };
    case 'PENDING_DEPT_HEAD':
    default:
      return { label: 'Pending Dept Head', className: 'pending' };
  }
};

// Skeleton Loader Component
const TableSkeleton = () => {
  return (
    <div className="leave-status-table-skeleton">
      {[...Array(5)].map((_, index) => (
        <div key={index} className="leave-status-skeleton-row">
          <div className="leave-status-skeleton-cell leave-status-skeleton-text" style={{ width: '120px' }}></div>
          <div className="leave-status-skeleton-cell leave-status-skeleton-text" style={{ width: '100px' }}></div>
          <div className="leave-status-skeleton-cell leave-status-skeleton-text" style={{ width: '80px' }}></div>
          <div className="leave-status-skeleton-cell leave-status-skeleton-text" style={{ width: '180px' }}></div>
          <div className="leave-status-skeleton-cell leave-status-skeleton-text" style={{ width: '60px' }}></div>
          <div className="leave-status-skeleton-cell leave-status-skeleton-text" style={{ width: '100px' }}></div>
          <div className="leave-status-skeleton-cell leave-status-skeleton-badge"></div>
          <div className="leave-status-skeleton-cell leave-status-skeleton-action"></div>
        </div>
      ))}
    </div>
  );
};

// Status Icon Component
const StatusIcon = ({ className }) => {
  if (className === 'approved') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 6L9 17l-5-5"></path>
      </svg>
    );
  }
  if (className === 'rejected') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  );
};

const LeaveStatus = () => {
  const dispatch = useDispatch();
  const { employeeLeaves, employeeLeavesLoading, leaveSummary } = useSelector(
    (state) => state.leave
  );

  const [filter, setFilter] = useState('all');
  const [selectedLeave, setSelectedLeave] = useState(null);

  useEffect(() => {
    dispatch(fetchEmployeeLeaves());
    dispatch(fetchLeaveSummary());
  }, [dispatch]);

  const getFilteredLeaves = () => {
    if (filter === 'all') return employeeLeaves;

    return employeeLeaves.filter((leave) => {
      switch (filter) {
        case 'pending':
          return leave.status === 'PENDING_DEPT_HEAD' || leave.status === 'PENDING_HR';
        case 'approved':
          return leave.status === 'APPROVED';
        case 'rejected':
          return leave.status === 'REJECTED_BY_DEPT_HEAD' || leave.status === 'REJECTED_BY_HR';
        default:
          return true;
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const filteredLeaves = getFilteredLeaves();
  const counts = {
    all:      leaveSummary.totalLeaves,
    pending:  leaveSummary.pending,
    approved: leaveSummary.approved,
    rejected: leaveSummary.rejected,
  };

  return (
    <div className='index-box'>
      <Sidebar />
      <div className="leave-status-container">
        <div className="leave-status-header">
          <div className="leave-status-header-content">
            <h2 className="leave-status-title">My Leave Requests</h2>
            <p className="leave-status-subtitle">
              Track your leave applications and approval status
            </p>
          </div>
          <Link to="/employee/leave/apply" className="leave-status-apply-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Apply Leave
          </Link>
        </div>

        {/* Filter Tabs */}
        <div className="leave-status-filters">
          <div className="leave-status-filter-tabs">
            {['all','pending','approved','rejected'].map((key) => (
              <button
                key={key}
                className={`leave-status-filter-tab ${filter === key ? 'leave-status-filter-tab-active' : ''}`}
                onClick={() => setFilter(key)}
              >
                <span className="leave-status-filter-tab-label">
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </span>
                <span className="leave-status-filter-tab-count">({counts[key]})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Leave Table */}
        {employeeLeavesLoading ? (
          <div className="leave-status-table-wrapper">
            <TableSkeleton />
          </div>
        ) : filteredLeaves.length === 0 ? (
          <div className="leave-status-empty">
            <div className="leave-status-empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <p className="leave-status-empty-text">No leave requests found</p>
          </div>
        ) : (
          <div className="leave-status-table-wrapper">
            <table className="leave-status-table">
              <thead className="leave-status-table-head">
                <tr>
                  <th className="leave-status-table-header">Leave Type</th>
                  <th className="leave-status-table-header">Planning</th>
                  <th className="leave-status-table-header">Duration</th>
                  <th className="leave-status-table-header">Dates</th>
                  <th className="leave-status-table-header">Days</th>
                  <th className="leave-status-table-header">Applied</th>
                  <th className="leave-status-table-header">Status</th>
                  <th className="leave-status-table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="leave-status-table-body">
                {filteredLeaves.map((leave) => {
                  const status = getLeaveStatus(leave);
                  return (
                    <tr key={leave.id} className="leave-status-table-row">
                      <td className="leave-status-table-cell">
                        <span className="leave-status-type-badge">
                          {LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType}
                        </span>
                      </td>
                      <td className="leave-status-table-cell">
                        <span className={`leave-status-planning-badge leave-status-planning-${(leave.planningType || '').toLowerCase()}`}>
                          {PLANNING_LABELS[leave.planningType] || leave.planningType || 'N/A'}
                        </span>
                      </td>
                      <td className="leave-status-table-cell">
                        <span className="leave-status-duration-text">
                          {DURATION_LABELS[leave.duration] || leave.duration}
                        </span>
                      </td>
                      <td className="leave-status-table-cell">
                        <div className="leave-status-date-range">
                          <span className="leave-status-date-text">{formatDate(leave.fromDate)}</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                          <span className="leave-status-date-text">{formatDate(leave.toDate)}</span>
                        </div>
                      </td>
                      <td className="leave-status-table-cell">
                        <span className="leave-status-days-badge">{leave.totalDays}</span>
                      </td>
                      <td className="leave-status-table-cell">
                        <span className="leave-status-applied-date">{formatDate(leave.appliedDate)}</span>
                      </td>
                      <td className="leave-status-table-cell">
                        <span className={`leave-status-badge leave-status-badge-${status.className}`}>
                          <StatusIcon className={status.className} />
                          {status.label}
                        </span>
                      </td>
                      <td className="leave-status-table-cell">
                        <button
                          className="leave-status-action-btn"
                          onClick={() => setSelectedLeave(leave)}
                          title="View Details"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Modal */}
        {selectedLeave && (
          <div className="leave-status-modal-overlay" onClick={() => setSelectedLeave(null)}>
            <div className="leave-status-modal" onClick={(e) => e.stopPropagation()}>
              <div className="leave-status-modal-header">
                <h3 className="leave-status-modal-title">Leave Request Details</h3>
                <button className="leave-status-modal-close" onClick={() => setSelectedLeave(null)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div className="leave-status-modal-body">
                {/* Leave Info */}
                <div className="leave-status-modal-section">
                  <h4 className="leave-status-modal-section-title">Leave Information</h4>
                  <div className="leave-status-modal-grid">
                    <div className="leave-status-modal-item">
                      <label className="leave-status-modal-label">Leave Type:</label>
                      <span className="leave-status-modal-value">
                        {LEAVE_TYPE_LABELS[selectedLeave.leaveType] || selectedLeave.leaveType}
                      </span>
                    </div>
                    <div className="leave-status-modal-item">
                      <label className="leave-status-modal-label">Planning:</label>
                      <span className={`leave-status-planning-badge leave-status-planning-${(selectedLeave.planningType||'').toLowerCase()}`}>
                        {PLANNING_LABELS[selectedLeave.planningType] || selectedLeave.planningType || 'N/A'}
                      </span>
                    </div>
                    <div className="leave-status-modal-item">
                      <label className="leave-status-modal-label">Duration:</label>
                      <span className="leave-status-modal-value">
                        {DURATION_LABELS[selectedLeave.duration] || selectedLeave.duration}
                      </span>
                    </div>
                    <div className="leave-status-modal-item">
                      <label className="leave-status-modal-label">From Date:</label>
                      <span className="leave-status-modal-value">{formatDate(selectedLeave.fromDate)}</span>
                    </div>
                    <div className="leave-status-modal-item">
                      <label className="leave-status-modal-label">To Date:</label>
                      <span className="leave-status-modal-value">{formatDate(selectedLeave.toDate)}</span>
                    </div>
                    <div className="leave-status-modal-item">
                      <label className="leave-status-modal-label">Days:</label>
                      <span className="leave-status-modal-value">
                        {selectedLeave.totalDays} {selectedLeave.totalDays === 0.5 ? 'day' : 'days'}
                      </span>
                    </div>
                    <div className="leave-status-modal-item">
                      <label className="leave-status-modal-label">Applied:</label>
                      <span className="leave-status-modal-value">{formatDate(selectedLeave.appliedDate)}</span>
                    </div>
                    <div className="leave-status-modal-item leave-status-modal-item-full">
                      <label className="leave-status-modal-label">Reason:</label>
                      <span className="leave-status-modal-value">{selectedLeave.reason || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Two-step Approval Timeline */}
                <div className="leave-status-modal-section">
                  <h4 className="leave-status-modal-section-title">Approval Status</h4>
                  <div className="leave-status-timeline">
                    {/* Step 1: Department Head */}
                    <div className={`leave-status-timeline-step ${
                      selectedLeave.deptHeadApproved ? 'leave-status-timeline-step-completed' :
                      selectedLeave.status === 'REJECTED_BY_DEPT_HEAD' ? 'leave-status-timeline-step-rejected' : 
                      'leave-status-timeline-step-pending'
                    }`}>
                      <div className="leave-status-timeline-indicator">
                        {selectedLeave.deptHeadApproved ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        ) : selectedLeave.status === 'REJECTED_BY_DEPT_HEAD' ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        ) : '1'}
                      </div>
                      <div className="leave-status-timeline-content">
                        <div className="leave-status-timeline-title">Department Head</div>
                        <div className="leave-status-timeline-status">
                          {selectedLeave.deptHeadApproved ? 'Approved' :
                           selectedLeave.status === 'REJECTED_BY_DEPT_HEAD' ? 'Rejected' : 'Pending'}
                        </div>
                        {selectedLeave.deptHeadRemark && (
                          <div className="leave-status-timeline-comment">"{selectedLeave.deptHeadRemark}"</div>
                        )}
                        {selectedLeave.deptHeadApprovedAt && (
                          <div className="leave-status-timeline-date">{formatDate(selectedLeave.deptHeadApprovedAt)}</div>
                        )}
                      </div>
                    </div>

                    <div className="leave-status-timeline-connector">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </div>

                    {/* Step 2: HR */}
                    <div className={`leave-status-timeline-step ${
                      selectedLeave.hrApproved ? 'leave-status-timeline-step-completed' :
                      selectedLeave.status === 'REJECTED_BY_HR' ? 'leave-status-timeline-step-rejected' :
                      selectedLeave.deptHeadApproved ? 'leave-status-timeline-step-pending' : 
                      'leave-status-timeline-step-disabled'
                    }`}>
                      <div className="leave-status-timeline-indicator">
                        {selectedLeave.hrApproved ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        ) : selectedLeave.status === 'REJECTED_BY_HR' ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        ) : '2'}
                      </div>
                      <div className="leave-status-timeline-content">
                        <div className="leave-status-timeline-title">HR Department</div>
                        <div className="leave-status-timeline-status">
                          {selectedLeave.hrApproved ? 'Approved' :
                           selectedLeave.status === 'REJECTED_BY_HR' ? 'Rejected' :
                           selectedLeave.deptHeadApproved ? 'Pending' : 'Awaiting Dept Head'}
                        </div>
                        {selectedLeave.hrRemark && (
                          <div className="leave-status-timeline-comment">"{selectedLeave.hrRemark}"</div>
                        )}
                        {selectedLeave.hrApprovedAt && (
                          <div className="leave-status-timeline-date">{formatDate(selectedLeave.hrApprovedAt)}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="leave-status-modal-footer">
                <button className="leave-status-modal-close-btn" onClick={() => setSelectedLeave(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveStatus;